import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { CompanySettings } from '~hooks/useCompanySettings/models';
import useCompanySettings from '~hooks/useCompanySettings/useCompanySettings';
import { RutterConnection } from '~hooks/useRutter/models';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import {
  CatalogedCompanySettingKey,
  companySettingCategoryLabelKeys,
  quickBooksPageCategories,
  settingKeysInCategory,
} from '~pages/Settings/Configuration/companySettingsCatalog';
import CompanySettingToggle from '~pages/Settings/Configuration/components/CompanySettingToggle';
import useVisibleCompanySettingKeys from '~pages/Settings/Configuration/useVisibleCompanySettingKeys';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import DefaultUntaxedTaxRateScheduleSetting from './DefaultUntaxedTaxRateScheduleSetting';
import LineDescriptionFormatSection from './LineDescriptionFormatSection';
import SharedConnectionSetting from './SharedConnectionSetting';

const I18N_PREFIX =
  'administration.integration.rutter.quick_books_online.advanced_configuration';

interface QuickBooksAdvancedConfigurationProps {
  connection?: RutterConnection | null;
}

// The QuickBooks page shows the company picked in the company selector, so its settings
// must be read and written for that company too - the store only holds the user's own.
const QuickBooksAdvancedConfiguration = observer(
  ({ connection }: QuickBooksAdvancedConfigurationProps) => {
    const { userStore } = useStores();
    const { getManagedCompanySettings } = useCompanySettings();
    const visibleSettingKeys = useVisibleCompanySettingKeys();
    const effectiveCompanyId = getEffectiveUserCompanyId(userStore);
    const isUserCompany = effectiveCompanyId === userStore.userCompany.id;
    const ownerExportSettings = connection?.managedByOwner
      ? connection.ownerExportSettings
      : null;
    const [managedCompanySettings, setManagedCompanySettings] =
      useState<CompanySettings | null>(null);
    const [didManagedFetchFail, setDidManagedFetchFail] = useState(false);

    useEffect(() => {
      if (isUserCompany) {
        return;
      }

      let isCurrentCompany = true;
      setManagedCompanySettings(null);
      setDidManagedFetchFail(false);

      const fetchManagedCompanySettings = async () => {
        try {
          const settings = await getManagedCompanySettings(effectiveCompanyId);
          if (isCurrentCompany && settings) {
            setManagedCompanySettings(settings);
          }
        } catch {
          // getManagedCompanySettings already toasted via handleRequestError
          if (isCurrentCompany) {
            setDidManagedFetchFail(true);
          }
        }
      };

      void fetchManagedCompanySettings();

      return () => {
        isCurrentCompany = false;
      };
    }, [effectiveCompanyId, getManagedCompanySettings, isUserCompany]);

    const companySettings = isUserCompany
      ? userStore.companySettings
      : managedCompanySettings;

    const ownerCheckedOverrides: Partial<Record<CatalogedCompanySettingKey, boolean>> =
      ownerExportSettings
        ? {
            accounting_platform_assigns_document_number:
              ownerExportSettings.accountingPlatformAssignsDocumentNumber,
            accounting_export_populates_invoice_service_date:
              ownerExportSettings.accountingExportPopulatesInvoiceServiceDate,
          }
        : {};

    return (
      <Box sx={{ maxWidth: '1100px' }}>
        <SettingsPageHeader
          description={t(`${I18N_PREFIX}.description`)}
          title={t(`${I18N_PREFIX}.title`)}
        />
        {didManagedFetchFail && (
          <Alert severity="error" sx={{ my: 2 }} variant="outlined">
            {t('error_messages.company_settings.failed_to_fetch')}
          </Alert>
        )}
        {!didManagedFetchFail && !companySettings && (
          <Box sx={{ py: 2 }}>
            <LoadingSpinner isVisible={true} loadingIndicatorSize={16} />
          </Box>
        )}
        <SharedConnectionSetting />
        {companySettings &&
          quickBooksPageCategories.map((category) => {
            const categorySettingKeys = settingKeysInCategory(
              visibleSettingKeys,
              category,
            );

            if (categorySettingKeys.length === 0) {
              return null;
            }

            return (
              <Box key={category} sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'normal' }}>
                  {t(companySettingCategoryLabelKeys[category])}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {categorySettingKeys.map((key) => (
                    <CompanySettingToggle
                      checkedOverride={ownerCheckedOverrides[key]}
                      companyId={effectiveCompanyId}
                      companySettings={companySettings}
                      disabled={ownerCheckedOverrides[key] !== undefined}
                      key={`${effectiveCompanyId}-${key}`}
                      settingKey={key}
                    />
                  ))}
                </Box>
                <Divider sx={{ mt: 2 }} />
              </Box>
            );
          })}
        <LineDescriptionFormatSection
          companyId={effectiveCompanyId}
          key={effectiveCompanyId}
          ownerFormats={ownerExportSettings?.lineDescriptionFormats}
        />
        {companySettings && (
          <DefaultUntaxedTaxRateScheduleSetting
            companyId={effectiveCompanyId}
            companySettings={companySettings}
            key={`default-untaxed-${effectiveCompanyId}`}
          />
        )}
      </Box>
    );
  },
);

export default QuickBooksAdvancedConfiguration;
